import $ from 'jquery'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'formstate'
const DATA_KEY = `opp.${NAME}`
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Selector = {
  DATA_FORM_STATE: 'form[data-form-state="on"]',
  FORM_STATE_ALERT_UNSAVED: '.form-state-alert-unsaved > *'
}

const State = {
  CLEAN: 'clean',
  DIRTY: 'dirty'
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FormState {
  constructor(element) {
    this._element = element
    this._supported = this._isSupported()
    this._alertUnsavedElement = this._getAlertUnsavedElement()
    this._snapshot = this._createInitialSnapshot()
    this._persisted = !this._isClean()

    // TODO Write internal state to hidden input on pagehide so that is can be
    // restored if persisted by the browser. This is necessary to distinguish
    // between changes made by us the last time the page was loaded and changes
    // made by the user. Our changes should be ignored, but the users changes
    // should not. Additionally, the snapshot will need to accomodate the fact
    // that the value of form controls which include the `autocomplete="off"`
    // attribute will not be persisted by the browser.

    // Handle form reset event to clear persisted flag.
    $(this._element).on('reset', () => this._reset())

    // Show an alert if browser persisted changes are present in form.
    $(window).on('pageshow', () => this._alertUnsaved())
  }

  // Getters

  static get Selector() {
    return Selector
  }

  // Public

  update(clean) {
    this._update(clean)
  }

  // Check the current state against the provided value.
  is(state) {
    return state === (this._isClean() ? State.CLEAN : State.DIRTY)
  }

  // Private

  _isSupported() {
    // N.B. Form Data is neither iterable nor array-like in IE.
    return 'FormData' in window && FormData.prototype[Symbol.iterator]
  }

  _getAlertUnsavedElement() {
    return $(this._element.querySelector(Selector.FORM_STATE_ALERT_UNSAVED)).appendTo('body')[0]
  }

  _createSnapshot(elem) {
    if (!this._supported) {
      return 'not-supported'
    }

    // N.B. file input will stringify to "{}" if present and "" if not.
    return JSON.stringify([...new FormData(elem || this._element)].sort())
  }

  _createInitialSnapshot() {
    if (!this._supported) {
      return 'not-supported'
    }

    const elem = this._element.cloneNode(true)
    elem.reset()

    return this._createSnapshot(elem)
  }

  _alertUnsaved() {
    if (!this._alertUnsavedElement || this._isClean()) {
      return
    }

    $(this._alertUnsavedElement).modal('show')
  }

  _isClean() {
    if (this._persisted) {
      return false
    }

    if (!this._supported) {
      return true
    }

    return this._snapshot === this._createSnapshot()
  }

  _reset() {
    // N.B. form is clean, no browser persisted changes remain.
    this._persisted = false
  }

  _update(clean) {
    this._snapshot = this._createSnapshot()
    if (clean) {
      // N.B. form is clean, no browser persisted changes remain.
      this._persisted = false
    }
  }

  // Static

  static update(sel) {
    FormState._jQueryInterface.call(
      $(sel || Selector.DATA_FORM_STATE),
      'update'
    )
  }

  static isDirty(sel) {
    return FormState._jQueryInterface.call(
      $(sel || Selector.DATA_FORM_STATE),
      'is',
      State.DIRTY
    )
  }

  static isClean(sel) {
    return FormState._jQueryInterface.call(
      $(sel || Selector.DATA_FORM_STATE),
      'is',
      State.CLEAN
    )
  }

  static _jQueryInterface(config, ...args) {
    const instances = [...this].map(el => {
      let data = $(el).data(DATA_KEY)
      if (!data) {
        data = new FormState(el)
        $(el).data(DATA_KEY, data)
      }

      return data
    })

    if (config === 'is') {
      const [state] = args
      if (state === State.CLEAN) {
        return instances.every(data => data.is(State.CLEAN))
      }

      return instances.some(data => data.is(state))
    }

    if (typeof config === 'string') {
      for (const data of instances) {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    }
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

FormState._jQueryInterface.call($(Selector.DATA_FORM_STATE))

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = FormState._jQueryInterface
$.fn[NAME].Constructor = FormState
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return FormState._jQueryInterface
}

export default FormState
